<div class="modal-header noselect">
  <h2 class="modal-title">{{ readOnly ? 'Work Item Details' : (isEdit ? 'Edit Work Item' : 'Add Work Item') }}</h2>
  <button type="button" class="btn-close" (click)="onCancel()">
    <span class="fa-solid fa-xmark"></span>
  </button>
</div>

<div class="modal-body">
  <!-- Customer rejection: read-only, with an explicit opt-in to clear it -->
  <div *ngIf="isEdit && workItem?.RejectionReason" class="rejection-banner">
    <div class="rejection-text noselect">
      <span class="fa-solid fa-circle-exclamation me-1"></span>
      Customer rejection: {{ workItem?.RejectionReason }}
    </div>
    <div *ngIf="!readOnly" class="form-check noselect" [formGroup]="workItemForm">
      <input class="form-check-input" type="checkbox" id="clearRejectionCheck" formControlName="clearRejection">
      <label class="form-check-label" for="clearRejectionCheck">
        Remove rejection when saving
      </label>
    </div>
  </div>

  <!-- View-only: plain details instead of a disabled form -->
  <div *ngIf="readOnly" class="read-only-details">
    <div class="detail-row">
      <span class="detail-label noselect">Job Title</span>
      <span class="detail-value">{{ workItem?.Title }}</span>
    </div>
    <div *ngIf="workItemVehicleLabel" class="detail-row">
      <span class="detail-label noselect">Vehicle</span>
      <span class="detail-value">{{ workItemVehicleLabel }}</span>
    </div>
    <div class="detail-row">
      <span class="detail-label noselect">Mechanic</span>
      <span class="detail-value">{{ assignedMechanicLabel }}</span>
    </div>
    <div *ngIf="workItem?.Miles != null" class="detail-row">
      <span class="detail-label noselect">Miles</span>
      <span class="detail-value">{{ workItem?.Miles | number }}</span>
    </div>
    <div *ngIf="workItem?.ShopHours != null" class="detail-row">
      <span class="detail-label noselect">Shop Time</span>
      <span class="detail-value">{{ formatShopTime(workItem!.ShopHours!) }}</span>
    </div>
    <div *ngIf="workItem?.PartsPricing != null" class="detail-row">
      <span class="detail-label noselect">Parts Pricing</span>
      <span class="detail-value">{{ workItem?.PartsPricing | currency }}</span>
    </div>
    <div *ngIf="workItem?.FlatFee != null" class="detail-row">
      <span class="detail-label noselect">Flat Fee</span>
      <span class="detail-value">
        {{ workItem?.FlatFee | currency }} ({{ workItem?.FlatFeeTaxable ? 'taxable' : 'no tax' }})
      </span>
    </div>
    <div *ngIf="workItem?.Notes" class="detail-row">
      <span class="detail-label noselect">Notes</span>
      <div class="detail-value notes-value">{{ workItem?.Notes }}</div>
    </div>
  </div>

  <!-- Editable form -->
  <form *ngIf="!readOnly" [formGroup]="workItemForm" (ngSubmit)="onSubmit()">
    <div *ngIf="!isEdit && vehicles.length > 1" class="mb-2">
      <label for="workItemModalVehicle" class="form-label noselect">Vehicle</label>
      <select id="workItemModalVehicle" class="form-select" formControlName="invoiceVehicleId">
        <option *ngFor="let vehicle of vehicles" [value]="vehicle.InvoiceVehicleId">
          {{ getVehicleDisplayName(vehicle) }}
        </option>
      </select>
    </div>

    <app-work-item-form-fields [form]="workItemForm" [mechanics]="mechanics"></app-work-item-form-fields>
  </form>

  <!-- Images for Upload: staged locally, uploaded once the work item is saved -->
  <div *ngIf="!readOnly && canEditImages" class="images-section">
    <h3 class="section-title noselect">Images for Upload</h3>

    <div *ngIf="pendingImages.length === 0" class="text-muted noselect mb-2">
      No images staged for upload.
    </div>

    <div class="image-grid">
      <div *ngFor="let pending of pendingImages; let i = index" class="image-card">
        <img [src]="pending.previewUrl" alt="Staged work item image" />
        <select class="form-select form-select-sm"
                [class.is-invalid]="imageValidationAttempted && !pending.imageTypeId"
                aria-label="Image type"
                [(ngModel)]="pending.imageTypeId"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="imageValidationAttempted = false">
          <option value="" disabled>Image type</option>
          <option *ngFor="let type of imageTypes" [value]="type.ImageTypeId">{{ type.ImageType }}</option>
        </select>
        <button type="button"
                class="btn-remove-image"
                title="Remove staged image"
                (click)="removePendingImage(i)">
          <span class="fa-solid fa-xmark"></span>
        </button>
      </div>
    </div>

    <!-- Hidden inputs: file-system picker and rear camera (mobile) -->
    <input #imageFileInput
           type="file"
           accept="image/*"
           class="d-none"
           aria-label="Choose image from files"
           (change)="onImageFileSelected($event)">
    <input #imageCameraInput
           type="file"
           accept="image/*"
           capture="environment"
           class="d-none"
           aria-label="Take a photo"
           (change)="onImageFileSelected($event)">

    <div class="image-source-buttons noselect">
      <button type="button" class="btn btn-outline-secondary" (click)="imageFileInput.click()">
        <span class="fa-solid fa-folder-open me-1"></span>Choose File
      </button>
      <button type="button" class="btn btn-outline-secondary" (click)="imageCameraInput.click()">
        <span class="fa-solid fa-camera me-1"></span>Take Photo
      </button>
    </div>
    <p class="upload-hint noselect">Staged images upload when the work item is saved.</p>
  </div>

  <!-- Attached Images: already uploaded to this work item -->
  <div *ngIf="isEdit" class="images-section">
    <h3 class="section-title noselect">Attached Images</h3>

    <div *ngIf="images.length === 0" class="text-muted noselect mb-2">
      No images attached to this work item.
    </div>

    <div class="image-grid">
      <div *ngFor="let image of images" class="image-card">
        <a [href]="image.ImageUrl" target="_blank" rel="noopener">
          <img [src]="image.ImageUrl" alt="Work item image" />
        </a>
        <select *ngIf="canEditImages"
                class="form-select form-select-sm"
                aria-label="Image type"
                [ngModel]="image.ImageTypeId"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="onImageTypeChange(image, $event)">
          <option *ngFor="let type of imageTypes" [value]="type.ImageTypeId">{{ type.ImageType }}</option>
        </select>
        <div *ngIf="!canEditImages" class="image-type-label noselect">
          {{ getImageTypeLabel(image.ImageTypeId) }}
        </div>
        <button *ngIf="canEditImages"
                type="button"
                class="btn-remove-image"
                title="Delete image"
                (click)="deleteImage(image)">
          <span class="fa-solid fa-xmark"></span>
        </button>
      </div>
    </div>
  </div>
</div>

<div class="modal-footer noselect">
  <button *ngIf="isEdit && allowDelete && !readOnly" type="button" class="btn btn-outline-danger me-auto" (click)="onDelete()">
    <span class="fa-solid fa-trash-can me-1"></span>Delete Work Item
  </button>
  <button type="button" class="btn btn-secondary" (click)="onCancel()">
    {{ readOnly ? 'Close' : 'Cancel' }}
  </button>
  <button *ngIf="!readOnly" type="submit" class="btn btn-primary" (click)="onSubmit()">
    {{ isEdit ? 'Save Changes' : 'Add Work Item' }}
  </button>
</div>
